// crewbit-admin/src/components/money/AgingHeroStrip.tsx
//
// 4-card hero strip for the admin Outstanding screen. One MetricCard per
// aging bucket — Current, 1–30, 31–60, 61+ — value tone-coded via
// bucketTone(). C3: segments are click-to-filter (onBucketClick) with the
// active bucket ringed.
//
// SPEC: docs/references/MONEY_MODULE_SPEC_v1.md §Outstanding.7.2.

import { MetricCard } from '../common/MetricCard';
import { palette, spacing, urgency } from '../../styles/tokens';
import { bucketTone, type AgingBucket } from '../../lib/aging';
import { formatMoney } from '../../lib/money';

interface BucketAmount {
  amount: number;
  count: number;
}

interface AgingHeroStripProps {
  buckets: Record<AgingBucket, BucketAmount>;
  totalOutstanding: number;
  onBucketClick?: (bucket: AgingBucket) => void;
  activeBucket?: AgingBucket | null;
}

const BUCKET_ORDER: AgingBucket[] = ['current', '1_30', '31_60', '61_plus'];

const BUCKET_LABEL: Record<AgingBucket, string> = {
  current: 'Current',
  '1_30': '1–30 days',
  '31_60': '31–60 days',
  '61_plus': '61+ days',
};

function valueColorForTone(tone: 'neutral' | 'warning' | 'danger'): string | undefined {
  switch (tone) {
    case 'neutral': return undefined;     // MetricCard default (admin.header)
    case 'warning': return urgency.warning;
    case 'danger':  return palette.red;
  }
}

export function AgingHeroStrip({
  buckets,
  totalOutstanding: _totalOutstanding,
  onBucketClick,
  activeBucket,
}: AgingHeroStripProps) {
  return (
    <div style={{
      display: 'flex',
      gap: spacing.md,
      flexWrap: 'wrap',
    }}>
      {BUCKET_ORDER.map((bucket) => {
        const data = buckets[bucket];
        const tone = bucketTone(bucket);
        const sub = `${data.count} invoice${data.count === 1 ? '' : 's'}`;
        return (
          <MetricCard
            key={bucket}
            label={BUCKET_LABEL[bucket]}
            value={formatMoney(data.amount)}
            valueColor={valueColorForTone(tone)}
            sub={sub}
            onClick={onBucketClick ? () => onBucketClick(bucket) : undefined}
            active={activeBucket === bucket}
          />
        );
      })}
    </div>
  );
}
